import { Button, Divider, HStack, Spinner, Stack, Text } from "@chakra-ui/react";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ContributingEntityStatus,
  useAcceptContributingEntityInviteMutation,
  useDeclineContributingEntityInviteMutation,
  useGetContributingEntityInviteQuery,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useLogout } from "../../../common/auth/useLogout";
import { isApiErrorDto } from "../../../common/errors/isApiErrorDto";
import { isFetchBaseQueryError } from "../../../common/errors/isFetchBaseQueryError";
import { useApiError } from "../../../common/errors/useApiError";
import { useContributionUrls } from "../../sourcing-events/portal/contributing/useContributionUrls";
import { ContributingEntityInviteFacts } from "../ContributingEntityInviteFacts";

interface Props {
  inviteId: string;
  secret: string;
}

type Failure = FetchBaseQueryError | SerializedError;

export const AcceptContributingEntityInvite = ({ inviteId, secret }: Props) => {
  const contributionUrls = useContributionUrls();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useLogout();
  const displayer = useApiError();
  const organizationName = useLoggedInWithOrgContextState().selectedOrg.name;
  const { data, error } = useGetContributingEntityInviteQuery({ inviteId, secret });
  const [acceptInvite, { isLoading: isAccepting }] = useAcceptContributingEntityInviteMutation();
  const [declineInvite, { isLoading: isDeclining }] = useDeclineContributingEntityInviteMutation();
  const [failure, setFailure] = useState<Failure | null>(null);
  const [hasDeclined, setHasDeclined] = useState(false);

  const accept = async () => {
    try {
      const detail = await acceptInvite({ inviteId, secret }).unwrap();
      navigate(contributionUrls.view(detail.entity.id), { replace: true });
    } catch (e) {
      setFailure(e as Failure);
    }
  };

  const decline = async () => {
    try {
      await declineInvite({ inviteId, secret, declineContributingEntityInviteRequestDto: {} }).unwrap();
      setHasDeclined(true);
    } catch (e) {
      displayer.trigger(e);
    }
  };

  const myContributions = (
    <Button variant="link" onClick={() => navigate(contributionUrls.list)}>
      {t("My contributions")}
    </Button>
  );

  if (failure) {
    return (
      <Stack spacing="4">
        {displayer.show(failure)}
        <Text fontSize="sm">
          {t("If the invitation was sent to someone else, sign in as that person and open the emailed link again.")}
        </Text>
        <Button colorScheme="teal" onClick={() => void logout()}>
          {t("Sign in as another user")}
        </Button>
        {myContributions}
      </Stack>
    );
  }

  if (error) {
    const code = isFetchBaseQueryError(error) && isApiErrorDto(error.data) ? error.data.errorCode : undefined;
    if (code === "ContributingEntityInviteAlreadyAccepted") {
      return (
        <Stack spacing="4">
          <Text fontSize="2xl" fontWeight="bold">
            {t("The invitation has already been accepted")}
          </Text>
          <Text>{t("If {{organization}} accepted it, the contribution is open to you.", { organization: organizationName })}</Text>
          <Button colorScheme="teal" onClick={accept} isLoading={isAccepting}>
            {t("Open the contribution")}
          </Button>
          {myContributions}
        </Stack>
      );
    }
    return (
      <Stack spacing="4">
        <Text fontSize="2xl" fontWeight="bold">
          {code === "ContributingEntityInviteRateLimited"
            ? t("Too many attempts")
            : code === "ContributingEntityNotFound"
              ? t("The invitation has been withdrawn")
              : t("This invitation link is not valid")}
        </Text>
        <Text>
          {code === "ContributingEntityInviteRateLimited"
            ? t("Wait a few minutes, then open the link from your email again.")
            : code === "ContributingEntityNotFound"
              ? t("The supplier who invited you has removed your company from the offer.")
              : t("Open the most recent link from your email, or ask the supplier to invite you again.")}
        </Text>
        {myContributions}
      </Stack>
    );
  }

  if (!data) {
    return (
      <Stack spacing="4" align="center">
        <Spinner color="smPrimary" />
        <Text fontSize="sm">{t("Opening the invitation")}...</Text>
      </Stack>
    );
  }

  if (hasDeclined || data.status === ContributingEntityStatus.InvitationDeclined) {
    return (
      <Stack spacing="4">
        <ContributingEntityInviteFacts invite={data} />
        <Text fontSize="md" fontWeight="semibold">
          {t("You have declined this invitation. {{leadSupplier}} will see it on their offer.", {
            leadSupplier: data.leadSupplierName,
          })}
        </Text>
        {myContributions}
      </Stack>
    );
  }

  return (
    <Stack spacing="0">
      <ContributingEntityInviteFacts invite={data} />
      <Text fontSize="md" mt="20px">
        {t("You accept on behalf of {{organization}}, the organization you are working in.", {
          organization: organizationName,
        })}
      </Text>
      <Button colorScheme="teal" mt="20px" onClick={accept} isLoading={isAccepting}>
        {t("Accept invitation")}
      </Button>
      <Divider my="4" />
      <HStack>
        <Text fontSize="sm" color="smMuted">
          {t("Not participating in this offer?")}
        </Text>
        <Button variant="link" colorScheme="red" onClick={decline} isLoading={isDeclining}>
          {t("Decline the invitation")}
        </Button>
      </HStack>
    </Stack>
  );
};
